import { 
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  updateProfile,
  GoogleAuthProvider,
  signInWithPopup,
  getAdditionalUserInfo
} from 'firebase/auth';
import { auth } from '../config/firebase';
import { createUser, getUserById } from './userService';
import { AUDIT_ACTIONS, createAuditLog } from './auditService';
// import { sendWelcomeEmail } from './emailService'; // Desabilitado para economizar quota

/**
 * Registrar novo usuário
 */
export const registerUser = async (email, password, userData) => {
  try {
    // Criar usuário no Firebase Auth
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;

    // Atualizar perfil com nome
    if (userData.name) {
      await updateProfile(user, {
        displayName: userData.name
      });
    }

    // Criar documento do usuário no Firestore
    const result = await createUser(user.uid, {
      name: userData.name || 'Usuário',
      email: email,
      role: userData.role || 'user',
      phone: userData.phone || '',
      barbershopId: userData.role === 'owner' ? userData.barbershopId : null,
      createdAt: new Date().toISOString()
    });

    if (!result.success) {
      throw new Error('Erro ao criar perfil do usuário');
    }

    await createAuditLog({
      action: AUDIT_ACTIONS.REGISTER,
      userId: user.uid,
      userName: userData.name || user.displayName || email,
      userEmail: email,
      userRole: userData.role || 'user',
      targetType: 'user',
      targetId: user.uid,
      targetName: userData.name || email,
      description: `Nova conta criada com e-mail (${userData.role || 'user'})`,
      metadata: { provider: 'password' }
    });

    // Email de boas-vindas removido para economizar quota
    // Usuário recebe confirmação visual no app

    return { 
      success: true, 
      user: user,
      message: 'Conta criada com sucesso!' 
    };
  } catch (error) {
    console.error('Error in registerUser:', error);
    
    // Traduzir erros do Firebase
    let message = 'Erro ao criar conta';
    if (error.code === 'auth/email-already-in-use') {
      message = 'Este email já está cadastrado';
    } else if (error.code === 'auth/weak-password') {
      message = 'A senha deve ter pelo menos 6 caracteres';
    } else if (error.code === 'auth/invalid-email') {
      message = 'Email inválido';
    }
    
    return { success: false, error: message };
  }
};

/**
 * Login com email e senha
 */
export const loginUser = async (email, password) => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;

    // Buscar dados do usuário no Firestore
    const result = await getUserById(user.uid);
    
    if (!result.success) {
      throw new Error('Usuário não encontrado no sistema');
    }

    await createAuditLog({
      action: AUDIT_ACTIONS.LOGIN,
      userId: user.uid,
      userName: result.data.name || user.displayName || user.email,
      userEmail: result.data.email || user.email,
      userRole: result.data.role || 'user',
      targetType: 'session',
      targetId: user.uid,
      targetName: result.data.name || user.email,
      description: 'Login realizado com e-mail e senha',
      metadata: { provider: 'password' }
    });

    return { 
      success: true, 
      user: user,
      userData: result.data,
      message: 'Login realizado com sucesso!' 
    };
  } catch (error) {
    console.error('Error in loginUser:', error);
    
    let message = 'Erro ao fazer login';
    if (error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password') {
      message = 'Email ou senha incorretos';
    } else if (error.code === 'auth/invalid-email') {
      message = 'Email inválido';
    } else if (error.code === 'auth/too-many-requests') {
      message = 'Muitas tentativas. Tente novamente mais tarde';
    }
    
    return { success: false, error: message };
  }
};

/**
 * Login com Google
 */
export const loginWithGoogle = async () => {
  try {
    const provider = new GoogleAuthProvider();
    const userCredential = await signInWithPopup(auth, provider);
    const user = userCredential.user;
    const isNewUser = Boolean(getAdditionalUserInfo(userCredential)?.isNewUser);

    // Verificar se usuário já existe no Firestore
    let result = await getUserById(user.uid);
    
    if (!result.success) {
      // Criar novo usuário no Firestore
      await createUser(user.uid, {
        name: user.displayName || 'Usuário',
        email: user.email,
        role: 'user',
        phone: user.phoneNumber || '',
        photoURL: user.photoURL || '',
        createdAt: new Date().toISOString()
      });
      
      result = await getUserById(user.uid);
    }

    await createAuditLog({
      action: isNewUser ? AUDIT_ACTIONS.REGISTER : AUDIT_ACTIONS.LOGIN_GOOGLE,
      userId: user.uid,
      userName: result.data?.name || user.displayName || user.email,
      userEmail: result.data?.email || user.email,
      userRole: result.data?.role || 'user',
      targetType: 'session',
      targetId: user.uid,
      targetName: result.data?.name || user.email,
      description: isNewUser ? 'Nova conta criada com Google' : 'Login realizado com Google',
      metadata: { provider: 'google.com' }
    });

    return { 
      success: true, 
      user: user,
      userData: result.data,
      message: 'Login realizado com sucesso!' 
    };
  } catch (error) {
    console.error('Error in loginWithGoogle:', error);
    
    let message = 'Erro ao fazer login com Google';
    if (error.code === 'auth/popup-closed-by-user') {
      message = 'Login cancelado';
    } else if (error.code === 'auth/popup-blocked') {
      message = 'Popup bloqueado pelo navegador';
    }
    
    return { success: false, error: message };
  }
};

/**
 * Logout
 */
export const logoutUser = async () => {
  try {
    const user = auth.currentUser;
    if (user) {
      const result = await getUserById(user.uid);
      await createAuditLog({
        action: AUDIT_ACTIONS.LOGOUT,
        userId: user.uid,
        userName: result.data?.name || user.displayName || user.email,
        userEmail: result.data?.email || user.email,
        userRole: result.data?.role || 'user',
        targetType: 'session',
        targetId: user.uid,
        targetName: result.data?.name || user.email,
        description: 'Logout realizado'
      });
    }
    await signOut(auth);
    return { success: true, message: 'Logout realizado com sucesso!' };
  } catch (error) {
    console.error('Error in logoutUser:', error);
    return { success: false, error: 'Erro ao fazer logout' };
  }
};

/**
 * Resetar senha
 */
export const resetPassword = async (email) => {
  try {
    await sendPasswordResetEmail(auth, email);
    const user = auth.currentUser;
    if (user) {
      const result = await getUserById(user.uid);
      await createAuditLog({
        action: AUDIT_ACTIONS.PASSWORD_RESET_REQUEST,
        userId: user.uid,
        userName: result.data?.name || user.displayName || email,
        userEmail: email,
        userRole: result.data?.role || 'user',
        targetType: 'user',
        targetId: user.uid,
        targetName: result.data?.name || email,
        description: 'Solicitou e-mail de recuperação de senha'
      });
    }
    return { 
      success: true, 
      message: 'Email de recuperação enviado! Verifique sua caixa de entrada.' 
    };
  } catch (error) {
    console.error('Error in resetPassword:', error);
    
    let message = 'Erro ao enviar email de recuperação';
    if (error.code === 'auth/user-not-found') {
      message = 'Email não cadastrado';
    } else if (error.code === 'auth/invalid-email') {
      message = 'Email inválido';
    }
    
    return { success: false, error: message };
  }
};

/**
 * Verificar se usuário está autenticado
 */
export const getCurrentUser = () => {
  return auth.currentUser;
};
